/** Настройки интерфейса (индикаторы, тема). Хранятся локально у игрока; без хранилища — значения по умолчанию. */
import { useSyncExternalStore } from 'react';

export interface IndicatorSettings {
  sma: { on: boolean; period: number };
  ema: { on: boolean; period: number };
  vwap: { on: boolean };
  bb: { on: boolean; period: number; mult: number };
  rsi: { on: boolean; period: number };
  activity: { on: boolean };
}

export interface UiSettings {
  theme: 'dark' | 'light';
  indicators: IndicatorSettings;
  confirmFold: boolean;
}

const KEY = 'mt.settings.v1';

const DEFAULTS: UiSettings = {
  theme: 'dark',
  confirmFold: true,
  indicators: {
    sma: { on: true, period: 20 },
    ema: { on: false, period: 9 },
    vwap: { on: true },
    bb: { on: false, period: 20, mult: 2 },
    rsi: { on: false, period: 14 },
    activity: { on: true },
  },
};

function load(): UiSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const v = JSON.parse(raw) as Partial<UiSettings>;
    return { ...DEFAULTS, ...v, indicators: { ...DEFAULTS.indicators, ...(v.indicators ?? {}) } };
  } catch {
    return DEFAULTS;
  }
}

let current = load();
const listeners = new Set<() => void>();

export function updateSettings(fn: (s: UiSettings) => UiSettings) {
  current = fn(current);
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* без сохранения */
  }
  document.documentElement.dataset.theme = current.theme;
  listeners.forEach((l) => l());
}

export function useSettings(): UiSettings {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}

export function applyInitialTheme() {
  document.documentElement.dataset.theme = current.theme;
}
